import React, { useEffect, useState } from "react";
import "./ProjectLoader.css";
import { COMPANY_LOGO as companyLogo, PROJECT_LOGO } from "./brandAssets";

/**
 * Full-screen loader used across the web app (route loads, the tour's first
 * panorama, the master plan image). `progress` (0..1) shows a real bar and a
 * percentage; without it the bar sweeps.
 */
// Must match the duration of project-loader-progress in ProjectLoader.css.
const SWEEP_MS = 1200;

export default function ProjectLoader({ progress = null, label = "Preparing your experience", done = false }) {
  // With `done`, stay mounted long enough to fade out over the content.
  const [removed, setRemoved] = useState(false);
  useEffect(() => {
    if (!done) {
      setRemoved(false);
      return undefined;
    }
    const timer = setTimeout(() => setRemoved(true), 400);
    return () => clearTimeout(timer);
  }, [done]);

  if (removed) return null;

  const hasProgress = Number.isFinite(progress);
  const percent = hasProgress
    ? Math.max(4, Math.min(100, Math.round(progress * 100)))
    : null;

  return (
    <div className={`project-loader${done ? " is-done" : ""}`} role="status" aria-live="polite" aria-hidden={done || undefined}>
      <div className="project-loader__center">
        {/* Developer and project, side by side */}
        <div className="project-loader__brands">
          <img
            className="project-loader__company"
            src={companyLogo}
            alt="The House of Auroras"
            width={118}
            height={79}
          />
          <span className="project-loader__divider" aria-hidden="true" />
          <img
            className="project-loader__logo"
            src={PROJECT_LOGO}
            alt="The Aurora Hills"
            width={150}
            height={64}
          />
        </div>

        <div className="project-loader__track" aria-hidden="true">
          <div
            className={`project-loader__bar${hasProgress ? "" : " is-indeterminate"}`}
            style={
              hasProgress
                ? { width: `${percent}%` }
                : // Phase-locked to the page clock: when one loader hands off to
                  // the next (startup -> route -> screen), the sweep carries on
                  // instead of restarting, so it reads as a single loader.
                  { animationDelay: `-${Math.round(performance.now() % SWEEP_MS)}ms` }
            }
          />
        </div>

        <div className="project-loader__label">
          <span>{label}</span>
          {hasProgress && <span className="project-loader__percent">{percent}%</span>}
        </div>
      </div>

      {/* Footer credit, on every full-screen loader. */}
      <footer className="project-loader__credit">
        Powered by{" "}
        <a href="https://www.luminexa.in" target="_blank" rel="noopener noreferrer">
          Luminexa Technologies Pvt Ltd
        </a>
        <span aria-hidden="true">|</span>
        360° Interactive Virtual Tour
      </footer>
    </div>
  );
}

/** Small pill for short waits over content that stays visible (tour view switch). */
export function LoadingPill({ label }) {
  return (
    <div className="loading-pill" role="status" aria-live="polite">
      <span className="loading-pill__spinner" aria-hidden="true" />
      {label}
    </div>
  );
}
